import { z } from 'zod';

/**
 * Entidades do módulo Content (= ContentModule no monolith). Espelha os DTOs de
 * leitura do `ContentAdminReadController` (`/admin/content/*`) e os de escrita
 * dos controllers de ação. Schemas Zod + tipos inferidos para os recursos;
 * interfaces para inputs de criação/edição. Datas chegam como strings ISO no
 * JSON; uniões de string literais no lugar de enum (`erasableSyntaxOnly`).
 */

/** Resultado paginado padrão do back (`PaginatedResult<T>`). */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
}

export interface PaginationInput {
  page?: number;
  perPage?: number;
}

// ---------------------------------------------------------------------------
// Enums (uniões de string literais — espelham os enums do monolith)
// ---------------------------------------------------------------------------

export const CONTENT_TYPES = [
  'QUESTION',
  'EXAM',
  'VIDEO',
  'FLASHCARD',
  'OFFICIAL_COMMENT',
] as const;
export const ContentTypeSchema = z.enum(CONTENT_TYPES);
export type ContentType = z.infer<typeof ContentTypeSchema>;

export const CONTENT_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'] as const;
export const ContentStatusSchema = z.enum(CONTENT_STATUSES);
export type ContentStatus = z.infer<typeof ContentStatusSchema>;

/**
 * `REFERENCES` é o simulado apontando para questão de uma prova, sem ser dono
 * dela (o dono é a prova real, via `CONTAINS`) — ver
 * primum-monolith `docs/reference/modules/content.md` § Dono vs. apontador.
 * Fora desta lista, a ficha de qualquer questão apontada por simulado quebrava
 * inteira: o `ContentLinkSchema` abaixo recusa relação desconhecida no parse.
 */
export const CONTENT_RELATIONS = [
  'CONTAINS',
  'EXPLAINS',
  'REFERENCES',
] as const;
export const ContentRelationSchema = z.enum(CONTENT_RELATIONS);
export type ContentRelation = z.infer<typeof ContentRelationSchema>;

/** Prova real (já aplicada) vs. template de simulado da plataforma. */
export const EXAM_KINDS = ['REAL_EXAM', 'MOCK_EXAM'] as const;
export const ExamKindSchema = z.enum(EXAM_KINDS);
export type ExamKind = z.infer<typeof ExamKindSchema>;

/**
 * Trilha da residência a que a prova pertence. Eixo **diferente** do
 * `ExamKind` (real × simulado) e diferente da `SubjectView` — que também se
 * chama "R1"/"R+", mas é o recorte da árvore de assuntos por produto.
 */
export const EXAM_TYPES = [
  'DIRECT_ACCESS',
  'PREREQUISITE',
  'REVALIDA',
] as const;
export const ExamTypeSchema = z.enum(EXAM_TYPES);
export type ExamType = z.infer<typeof ExamTypeSchema>;

/**
 * Classificação conteúdo↔subject (fechado no back: enum ContentSubjectRelation).
 * PRIMARY: no máximo uma por conteúdo. SECONDARY: N. Default do back é 'PRIMARY'.
 */
export const SUBJECT_RELATIONS = ['PRIMARY', 'SECONDARY'] as const;
export const SubjectRelationSchema = z.enum(SUBJECT_RELATIONS);
export type SubjectRelation = z.infer<typeof SubjectRelationSchema>;

// ---------------------------------------------------------------------------
// Subjects
// ---------------------------------------------------------------------------

export const SubjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  deletedAt: z.string().nullable(),
  version: z.number(),
});
export type Subject = z.infer<typeof SubjectSchema>;

/**
 * O item da listagem admin (`GET /admin/content/subjects`). Só ela traz a
 * incidência: as rotas de escrita devolvem o `Subject` sem o campo.
 */
export const SubjectListItemSchema = SubjectSchema.extend({
  /**
   * Porcentagem já em 0–100. `0` é "calculado, e não caiu"; `null` é "sem
   * cálculo": o rebuild nunca rodou, ou o assunto nasceu depois do último.
   */
  generalIncidence: z.number().nullable(),
});
export type SubjectListItem = z.infer<typeof SubjectListItemSchema>;

export interface SubjectList extends Paginated<SubjectListItem> {
  /** O último rebuild da incidência, ISO; `null` se ele nunca rodou. */
  incidenceRebuiltAt: string | null;
}

export interface CreateSubjectInput {
  name: string;
}
export interface RenameSubjectInput {
  name: string;
}

// ---------------------------------------------------------------------------
// Subject views + memberships
// ---------------------------------------------------------------------------

export const SubjectViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  tenant: z.string(),
  version: z.number(),
});
export type SubjectView = z.infer<typeof SubjectViewSchema>;

export const SubjectViewMembershipSchema = z.object({
  id: z.string(),
  viewId: z.string(),
  subjectId: z.string(),
  /** Pai na árvore da view (subject-membro da mesma view). null = raiz. */
  parentSubjectId: z.string().nullable(),
  isLeaf: z.boolean(),
  version: z.number(),
});
export type SubjectViewMembership = z.infer<typeof SubjectViewMembershipSchema>;

export const SubjectViewDetailSchema = z.object({
  view: SubjectViewSchema,
  members: z.array(SubjectViewMembershipSchema),
});
export type SubjectViewDetail = z.infer<typeof SubjectViewDetailSchema>;

export interface CreateSubjectViewInput {
  name: string;
  tenant: string;
}
export interface AddMembershipInput {
  subjectId: string;
  /** Pai na árvore da view. Omitido/null = raiz. */
  parentSubjectId?: string | null;
  isLeaf?: boolean;
}
/** Reparent de um nó na árvore da view. `null` torna o nó raiz. */
export interface SetMembershipParentInput {
  parentSubjectId: string | null;
}

// ---------------------------------------------------------------------------
// Subject edges (grafo pai→filho)
// ---------------------------------------------------------------------------

export const SubjectEdgeSchema = z.object({
  id: z.string(),
  parentId: z.string(),
  childId: z.string(),
  version: z.number(),
});
export type SubjectEdge = z.infer<typeof SubjectEdgeSchema>;

export interface AddSubjectEdgeInput {
  parentId: string;
  childId: string;
}

// ---------------------------------------------------------------------------
// Content items + bodies por tipo
// ---------------------------------------------------------------------------

export const ContentItemSchema = z.object({
  id: z.string(),
  type: ContentTypeSchema,
  status: ContentStatusSchema,
  body: z.unknown(),
  deletedAt: z.string().nullable(),
  createdAt: z.string(),
  version: z.number(),
});
export type ContentItem = z.infer<typeof ContentItemSchema>;

/** Linha de `GET admin/content/items`: o item mais o rótulo da prova, pronto do back. */
export type ContentItemListEntry = ContentItem & {
  /** `formatExamLabel` da `EXAM`; `null` nos outros tipos. */
  label: string | null;
};

/** `body` polimórfico por tipo (intrínseco ao conteúdo). Espelha `bodies.ts`. */
/**
 * Afirmações do time de conteúdo **sobre** a questão — não mudam o que ela
 * pergunta. Espelha `QuestionEditorialFlags` do monolith (`bodies.ts`).
 *
 * Valem para os **dois** formatos, ao contrário de `annulled`: enunciado
 * ambíguo cabe recurso e diretriz muda numa dissertativa tanto quanto numa de
 * múltipla escolha. Ausente = `false` (o back só grava quando `true`).
 *
 * Nenhuma vem do CSV: o arquivo descreve a prova como foi aplicada, e estas são
 * julgamentos feitos depois, que mudam com o tempo.
 */
interface QuestionEditorialFlags {
  /** Cabe recurso contra ela (gabarito duvidoso, enunciado ambíguo). */
  appealable?: boolean;
  /** A conduta/diretriz mudou desde que a prova foi aplicada. */
  outdated?: boolean;
}

export type QuestionBody =
  | (QuestionEditorialFlags & {
      format: 'MULTIPLE_CHOICE';
      stem: string;
      alternatives: { id: string; text: string }[];
      /**
       * Ausente só na anulada: a importada da Aristo chega sem letra. A
       * anulada pelo admin continua com a dela.
       */
      correctAlternativeId?: string;
      /**
       * Anulada pela banca. Ausente = não anulada. A letra correta, quando
       * existe, continua guardada mesmo anulada — desanular devolve o gabarito
       * de antes; o que muda é só o que a leitura faz com ela.
       *
       * Só neste ramo: "não há alternativa correta a considerar" não significa
       * nada numa dissertativa.
       */
      annulled?: boolean;
    })
  | (QuestionEditorialFlags & {
      format: 'ESSAY';
      stem: string;
      /**
       * A resposta que o corretor espera, mesma gramática do `stem`. Ausente =
       * não cadastrada (o back não grava o campo vazio). Só neste ramo.
       */
      expectedAnswer?: string;
    });

export interface VideoBody {
  url: string;
  provider?: { name: string; externalId: string };
  durationSec?: number;
  transcript?: string;
}

export interface ExamBody {
  year: number;
  institutionId: string;
  variant: string;
  examKind: ExamKind;
  examType: ExamType;
}

export interface FlashcardBody {
  front: string;
  back: string;
}

export interface OfficialCommentBody {
  text: string;
}

export interface CreateContentItemInput {
  type: ContentType;
  body: Record<string, unknown>;
}

/**
 * Por que campo a listagem pode ordenar — espelha `CONTENT_ITEM_SORT_FIELDS`
 * do back, que recusa qualquer outro com 400.
 *
 * A lista é fechada nos dois lados pelo mesmo motivo: só campo top-level é
 * ordenável sem sort em memória sobre `body`, que é `Mixed` e não-indexado.
 * Ordenar por instituição/ano continua sendo pendência de back.
 */
export const CONTENT_ITEM_SORT_FIELDS = ['createdAt', 'updatedAt'] as const;
export type ContentItemSortField = (typeof CONTENT_ITEM_SORT_FIELDS)[number];

export interface ListContentItemsInput extends PaginationInput {
  type?: ContentType;
  /** Um status ou vários — o back aceita `status=DRAFT,PUBLISHED` em CSV. */
  status?: ContentStatus | ContentStatus[];
  includeDeleted?: boolean;
  /**
   * Ordenação **do banco**, não da página.
   *
   * Ordenar no cliente ordenaria só os 10 itens que vieram — "a mais recente"
   * seria a mais recente *daquela página*, o que é pior que não ordenar. Ausente
   * = o default do back (`createdAt` desc).
   */
  sortBy?: ContentItemSortField;
  sortDirection?: 'asc' | 'desc';
  /**
   * Filtros de questão. Qualquer um deles faz o back fixar `type = QUESTION`,
   * e com outro `type` ele responde 400.
   */
  questionFormat?: 'MULTIPLE_CHOICE' | 'ESSAY';
  /** `false` é "não anulada" (o back trata a chave ausente como não anulada). */
  annulled?: boolean;
  /** Só as questões desta prova. */
  examId?: string;
  /**
   * Filtros de prova (busca de provas, DESEN-41). Qualquer um deles faz o back
   * fixar `type = EXAM`; com filtro de questão junto, ele responde 400.
   */
  uf?: string;
  institutionId?: string;
  year?: number;
  /** O caderno, comparado exato no back. */
  variant?: string;
  examKind?: ExamKind;
}

/** As opções dos selects da busca de provas, para uma instituição. */
export interface ExamFilterOptions {
  /** Do mais novo para o mais velho. */
  years: number[];
  /** Em ordem alfabética; só os do ano, quando ele foi pedido. */
  variants: string[];
}

/**
 * O "o que buscar" da busca de questões (`GET admin/content/questions/search`),
 * um valor só — espelha `QUESTION_SEARCH_DEMANDS` do back, que recusa outro
 * com 400:
 * - `WITHOUT_COMMENT`: sem comentário oficial vivo;
 * - `REPORTED_COMMENT`: report aberto no comentário publicado;
 * - `REPORTED_QUESTION`: report aberto na própria questão.
 */
export const QUESTION_SEARCH_DEMANDS = [
  'WITHOUT_COMMENT',
  'REPORTED_COMMENT',
  'REPORTED_QUESTION',
] as const;
export type QuestionSearchDemand = (typeof QUESTION_SEARCH_DEMANDS)[number];

/** Os filtros da busca de questões. Todos escalares: o back recusa valor repetido. */
export interface SearchQuestionsInput extends PaginationInput {
  /** Grande Área, Especialidade ou Tema: o back inclui os descendentes. */
  subjectId?: string;
  uf?: string;
  institutionId?: string;
  year?: number;
  demand?: QuestionSearchDemand;
}

/** Um card da busca de questões: só questão `PUBLISHED`. */
export interface QuestionSearchCard {
  questionId: string;
  status: ContentStatus;
  /** A prova real dona da questão; `null` na questão avulsa. */
  exam: {
    examId: string;
    /** `null` quando a instituição da prova sumiu do cadastro. */
    institution: {
      institutionId: string;
      initials: string;
      uf: string;
      name: string;
    } | null;
    year: number;
    variant: string;
    /** O número da questão na prova, a partir de 1. */
    position: number | null;
  } | null;
  /** Texto puro do enunciado, até 200 caracteres. */
  excerpt: string;
  annulled: boolean;
  appealable: boolean;
  outdated: boolean;
  /** Grande Área > Especialidade > Tema do assunto `PRIMARY`; `[]` sem ele. */
  subjectPath: { id: string; name: string }[];
  openQuestionReports: number;
  openCommentReports: number;
  hasOfficialComment: boolean;
  /** Quem está editando a questão agora; `null` até o lock de edição chegar ao back. */
  editingBy: { name?: string } | null;
}

/**
 * As opções dos selects da busca de questões. `subjects` são os filhos do
 * assunto pedido (sem ele, as Grandes Áreas); `years`, os das provas reais.
 */
export interface QuestionFilterOptions {
  subjects: { id: string; name: string }[];
  years: number[];
}

export interface ListSubjectsInput extends PaginationInput {
  search?: string;
  includeDeleted?: boolean;
}

// ---------------------------------------------------------------------------
// Content links (conteúdo↔conteúdo) + classificações (conteúdo↔subject)
// ---------------------------------------------------------------------------

export const ContentSubjectLinkSchema = z.object({
  id: z.string(),
  contentId: z.string(),
  subjectId: z.string(),
  relation: SubjectRelationSchema,
  version: z.number(),
});
export type ContentSubjectLink = z.infer<typeof ContentSubjectLinkSchema>;

export const ContentLinkSchema = z.object({
  id: z.string(),
  sourceContentId: z.string(),
  targetContentId: z.string(),
  relation: ContentRelationSchema,
  position: z.number().nullable(),
  version: z.number(),
});
export type ContentLink = z.infer<typeof ContentLinkSchema>;

/**
 * O item do outro lado de um link, resolvido pelo back (`GetContentItemUseCase`).
 * `type` é `string`, não `ContentType`: o back tem tipos que o admin ainda não
 * lista (deck, pack), e a ficha mostra o tipo cru em vez de quebrar.
 * `label`/`examKind` só vêm numa prova.
 */
export const ContentLinkCounterpartSchema = z.object({
  type: z.string(),
  label: z.string().nullable(),
  examKind: ExamKindSchema.nullable(),
  /**
   * O começo do enunciado, em texto puro, quando o outro lado é uma questão
   * (a ficha da prova lista as questões por ele). `null` nos outros tipos.
   */
  excerpt: z.string().nullable().optional(),
});
export type ContentLinkCounterpart = z.infer<
  typeof ContentLinkCounterpartSchema
>;

/** Link na ficha: `counterpart = null` quando o outro lado não existe mais. */
export const ContentLinkDetailSchema = ContentLinkSchema.extend({
  counterpart: ContentLinkCounterpartSchema.nullable(),
});
export type ContentLinkDetail = z.infer<typeof ContentLinkDetailSchema>;

export const ContentItemDetailSchema = z.object({
  item: ContentItemSchema,
  /** Rótulo da própria prova (`SIGLA-UF ANO – VARIANTE`); `null` nos outros tipos. */
  label: z.string().nullable(),
  classifications: z.array(
    ContentSubjectLinkSchema.extend({
      /** `null` = o assunto não existe mais. */
      subjectName: z.string().nullable(),
    }),
  ),
  outgoingLinks: z.array(ContentLinkDetailSchema),
  incomingLinks: z.array(ContentLinkDetailSchema),
  /**
   * `fileId → URL` das imagens da questão/comentário. O `item.body` mantém os
   * tokens `{{img:<fileId>}}` (é o que a edição salva de volta); quem exibe
   * resolve com este mapa. Opcional só porque o back pode subir depois do
   * front — sem ele, a imagem aparece quebrada, não o token cru.
   */
  imageUrlsByFileId: z.record(z.string(), z.string()).optional(),
});
export type ContentItemDetail = z.infer<typeof ContentItemDetailSchema>;

export interface ClassifyContentInput {
  subjectId: string;
  relation?: SubjectRelation;
}
/** Troca a relação de uma classificação existente (PRIMARY ↔ SECONDARY). */
export interface SetContentSubjectRelationInput {
  relation: SubjectRelation;
}
/**
 * Reclassificar: repõe a classificação **inteira** do conteúdo de uma vez.
 *
 * A **ordem é a informação** — `subjectIds[0]` é a primária, o resto é
 * secundária —, e não há campo `relation` de propósito: seria uma segunda forma
 * de dizer o que a ordem já diz, e as duas poderiam discordar (duas primárias,
 * nenhuma primária). Lista vazia remove toda a classificação.
 *
 * É uma requisição, não três (`classify` + `setSubjectRelation` +
 * `unclassify`): o back troca o conjunto numa transação só, então a questão
 * nunca fica sem primária no meio do caminho.
 */
export interface SetContentClassificationInput {
  subjectIds: string[];
}
export interface LinkContentInput {
  targetContentId: string;
  relation: ContentRelation;
  position?: number;
}

// ---------------------------------------------------------------------------
// Instituições (referência das provas)
// ---------------------------------------------------------------------------

/** Unidades federativas — espelha o enum `UF` do monolith. */
export const UFS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;
export const UfSchema = z.enum(UFS);
export type Uf = z.infer<typeof UfSchema>;

export const InstitutionSchema = z.object({
  id: z.string(),
  name: z.string(),
  initials: z.string(),
  uf: UfSchema,
  /**
   * "As provas desta instituição são produzidas pela casa (Aristo)". Deixa as
   * provas dela fora da incidência geral; com prova apontando, o back recusa a
   * troca (`INSTITUTION_IN_HOUSE_IN_USE`).
   */
  isInHouse: z.boolean(),
  /** Arquivada quando não-nulo: sai das ofertas, continua resolvendo o rótulo. */
  deletedAt: z.string().nullable(),
  version: z.number(),
  /**
   * Quantas provas apontam para ela. É informação **e** precondição: arquivar
   * exige zero, e renomear com `examCount > 0` muda o contrato de planilhas já
   * preparadas.
   */
  examCount: z.number(),
});
export type Institution = z.infer<typeof InstitutionSchema>;

export interface ListInstitutionsInput extends PaginationInput {
  search?: string;
  includeDeleted?: boolean;
  isInHouse?: boolean;
}

export interface CreateInstitutionInput {
  name: string;
  initials: string;
  uf: Uf;
  isInHouse: boolean;
  /**
   * Confirma gravar mesmo havendo parecidas — o reenvio depois do 409
   * `INSTITUTION_SIMILAR_EXISTS`.
   */
  allowSimilar?: boolean;
}

export type UpdateInstitutionInput = CreateInstitutionInput;

/** Uma parecida do 409 `INSTITUTION_SIMILAR_EXISTS`: mesma sigla + UF, outro nome. */
export interface SimilarInstitution {
  id: string;
  initials: string;
  uf: Uf;
  name: string;
}

/**
 * Resultado da **mescla**: a origem some dentro do alvo (a primária) e as
 * provas dela trocam de instituição.
 *
 * O back devolve as duas linhas já atualizadas, então a tela não precisa
 * refetchar para mostrar o resultado — mas a invalidação acontece de qualquer
 * jeito, porque a coluna "Instituição" de **toda prova** também mudou.
 */
export const MergeInstitutionsResultSchema = z.object({
  /** O alvo, com o `examCount` somado. */
  target: InstitutionSchema,
  /** A origem, já arquivada e com `examCount` zero. */
  source: InstitutionSchema,
  movedExamCount: z.number(),
  /**
   * `false` = a mescla commitou, mas a reconstrução das projeções ouro falhou
   * depois do commit. **Não é erro** e não se repete a mescla: a prata está
   * certa, o ouro (incidência) segue com o id antigo até alguém rodar o
   * rebuild.
   */
  projectionsRebuilt: z.boolean(),
});
export type MergeInstitutionsResult = z.infer<
  typeof MergeInstitutionsResultSchema
>;

/**
 * Uma colisão de coordenada que fez a mescla ser recusada — o `details` do 409
 * `INSTITUTION_MERGE_CONFLICT`.
 *
 * Duas provas, uma em cada instituição, com o mesmo `(ano, variante)`. Mesclar
 * criaria duas provas idênticas na mesma instituição, e isso não é barrado por
 * índice único hoje — então o back recusa o lote inteiro e devolve a lista para
 * a pessoa resolver uma por uma.
 */
export interface InstitutionMergeCollision {
  year: number;
  variant: string;
  /** A prova que **sairia** da origem. */
  sourceExamContentId: string;
  /** A que o alvo **já tem** naquela coordenada. */
  targetExamContentId: string;
}

// ---------------------------------------------------------------------------
// Provas: importação, preview e gestão das questões
// ---------------------------------------------------------------------------

/**
 * Uma imagem citada pelo texto da questão. `filename` é o nome exatamente como
 * aparece no token `[](imagens/<filename>)`; `base64` aceita data URI ou cru.
 */
export interface QuestionImageInput {
  filename: string;
  base64: string;
}

export interface CreateExamQuestionInput {
  /** Ausente = ao fim da prova. */
  position?: number;
  format: 'MULTIPLE_CHOICE' | 'ESSAY';
  /** Com os tokens `[](imagens/x.png)` crus — o back é que os resolve. */
  stem: string;
  alternatives?: { id: string; text: string }[];
  correctAlternativeId?: string;
  /**
   * Só `ESSAY`: a resposta que o corretor espera, com os tokens de imagem crus
   * como o `stem` — as imagens dela vão em `images`, junto das do enunciado.
   */
  expectedAnswer?: string;
  images?: QuestionImageInput[];
  /**
   * Marcações editoriais. **Não vêm do CSV** (não há coluna) — quem as manda é
   * o modal manual "criar questão", que usa este mesmo endpoint.
   */
  appealable?: boolean;
  outdated?: boolean;
  /** O comentário oficial, com tokens de imagem crus. Vazio = sem comentário. */
  comment?: string;
  /** Quem do time escreveu o comentário. */
  commentAuthor?: string;
  /** Imagens citadas pelo texto do comentário — lista à parte das da questão. */
  commentImages?: QuestionImageInput[];
  /**
   * A célula `classification` do CSV como veio: nomes de assunto separados por
   * `|`, o primeiro é a primária. Quem separa e resolve nome → assunto é o
   * back — pelo mesmo motivo da instituição, que também vai inteira.
   */
  classification?: string;
}

/** Questão criada + a aresta que a prende à prova (traz a `position` efetiva). */
export interface ExamQuestion {
  question: ContentItem;
  link: ContentLink;
}

/**
 * Uma classificação da questão, com o nome do assunto **já resolvido pelo
 * back**. O nome vem no payload em vez de o front traduzir o UUID pela lista de
 * assuntos: o preview mostra a classificação de até 80 questões de uma vez, e a
 * alternativa seria carregar a taxonomia inteira só para isso — mostrando UUID
 * enquanto ela não chega.
 */
export interface ExamPreviewClassification {
  subjectId: string;
  /** `null` = o assunto foi removido depois de a questão ser classificada. */
  subjectName: string | null;
  relation: SubjectRelation;
}

/**
 * O comentário oficial da questão — **um por questão**. É um `ContentItem`
 * próprio no back (tipo `OFFICIAL_COMMENT`, ligado por `EXPLAINS`), mas chega
 * achatado: a tela precisa do texto e do crédito, não do envelope.
 */
export interface ExamPreviewOfficialComment {
  id: string;
  status: string;
  /** Já com `<img>` real no lugar do token, como o corpo da questão. */
  text: string;
  /** `null` = comentário sem atribuição. */
  author: string | null;
}

export interface ExamPreviewQuestion {
  position: number;
  /** `stem`/alternativas já com `<img>` real no lugar do token de imagem. */
  question: ContentItem;
  /** Primária primeiro, secundárias em seguida por nome. */
  classifications: ExamPreviewClassification[];
  /** `null` = a questão ainda não tem comentário oficial. */
  officialComment: ExamPreviewOfficialComment | null;
}

export interface ExamPreview {
  exam: ContentItem;
  questions: ExamPreviewQuestion[];
  /**
   * `fileId → URL` de toda imagem citada pelas questões da prova. O card de
   * leitura não precisa (o `<img>` já vem pronto no `body`); quem usa é o modo
   * edição, que edita o texto **com** os tokens `{{img:<fileId>}}` dentro — é
   * isso que é salvo de volta — e sem o mapa não teria como mostrar as imagens
   * já salvas nem o preview da questão em edição.
   */
  imageUrlsByFileId: Record<string, string>;
}

/**
 * Gabarito de uma questão: letra correta e/ou "anulada". Pelo menos um dos dois.
 *
 * Endpoint próprio, e não o `PATCH` de body inteiro, porque o body que a tela
 * tem em mão é o do preview — onde cada imagem já virou `<img src="…">`, que o
 * back não aceita de volta (apagaria as imagens da questão).
 */
export interface SetQuestionAnswerKeyInput {
  correctAlternativeId?: string;
  annulled?: boolean;
}

/**
 * O comentário oficial de uma questão, como **estado final**: o back decide
 * entre criar, atualizar e remover. `text` vazio remove — apagar o texto e
 * salvar é o gesto natural de quem quer tirar o comentário, e um endpoint a
 * mais para o mesmo gesto seria uma segunda forma de dizer a mesma coisa.
 *
 * O texto vai com os tokens `{{img:<fileId>}}` **crus**, como o body da
 * questão: `<img>` não está no allowlist de escrita do back e reenviá-lo
 * apagaria as imagens.
 *
 * **Sem autor**: quem salva assina, e o back resolve isso do token.
 */
export interface SetQuestionOfficialCommentInput {
  text: string;
  images?: QuestionImageInput[];
  /**
   * Por onde o texto entrou, para o `audit` do comentário. Ausente, o back
   * grava `ADMIN_FORM`.
   */
  origin?: OfficialCommentOrigin;
}

/**
 * O que o cliente pode declarar como origem no salvar do comentário. Espelha
 * `CLIENT_DECLARABLE_AUDIT_ORIGINS` (primum-monolith, content): `HARRISON`
 * quando o texto saiu de um rascunho do Harrison (DESEN-34).
 */
export type OfficialCommentOrigin = 'ADMIN_FORM' | 'HARRISON';

/**
 * A página do comentário oficial: a questão e o comentário juntos. Espelha
 * `OfficialCommentWorkspaceOutputDto` (primum-monolith, content).
 *
 * A questão vem com `<img>` já resolvido, como no preview; o comentário vem com
 * os tokens `{{img:<fileId>}}` **crus**, porque é o que o editor salva de volta.
 * O `imageUrlsByFileId` é quem transforma os tokens em imagem na leitura e no
 * editor.
 */
export interface OfficialCommentWorkspace {
  question: {
    id: string;
    status: string;
    format: 'MULTIPLE_CHOICE' | 'ESSAY';
    stem: string;
    alternatives: { id: string; text: string }[];
    /** `null` na dissertativa e na anulada sem letra divulgada. */
    correctAlternativeId: string | null;
    annulled: boolean;
    expectedAnswer: string | null;
    subjects: ExamPreviewClassification[];
    institution: {
      institutionId: string;
      initials: string;
      uf: string;
      name: string;
    } | null;
    year: number | null;
  };
  /** `null` = a questão não tem comentário vivo (diferente do 404 da questão). */
  comment: {
    id: string;
    /** Com `{{img:<fileId>}}`. */
    text: string;
    status: ContentStatus;
    updatedAt: string;
    /** Quem salvou o texto por último. Nunca o e-mail. */
    authorName: string | null;
  } | null;
  /** Reports `OPEN` do comentário. */
  openReports: number;
  imageUrlsByFileId: Record<string, string>;
}

/** Uma linha do CSV, sem bytes de imagem — payload do dry-run de importação. */
export interface ValidateExamImportRow {
  position: number;
  format: 'MULTIPLE_CHOICE' | 'ESSAY';
  stem: string;
  alternatives?: { id: string; text: string }[];
  correctAlternativeId?: string;
  /** Só `ESSAY`. */
  expectedAnswer?: string;
  /** Só os nomes citados nos tokens da linha. */
  imageFilenames: string[];
  /** Nomes de assunto separados por `|`, o primeiro é a primária. */
  classification?: string;
  comment?: string;
  commentAuthor?: string;
  commentImageFilenames?: string[];
}

export interface ValidateExamImportInput {
  /** `(SIGLA-UF) NOME` — a coluna `institution` do CSV inteira. */
  institutionLabel: string;
  examYear: number;
  examVariant: string;
  rows: ValidateExamImportRow[];
}

export interface ValidateExamImportError {
  position: number;
  message: string;
}

/**
 * Por que não basta `institution: null`: as razões pedem ações opostas do
 * admin — corrigir a planilha ou cadastrar a instituição.
 *
 * A instituição se acha pela chave inteira (sigla, UF, nome), sem caixa e sem
 * acento. `NAME_MISMATCH` é quando só há parecida — mesma sigla + UF, outro
 * nome — e a mensagem traz o nome cadastrado. **Barra o lote** como as outras.
 */
export type ValidateExamImportInstitutionErrorReason =
  | 'MALFORMED_LABEL'
  | 'NOT_FOUND'
  | 'NAME_MISMATCH';

export interface ValidateExamImportResult {
  institution: {
    id: string;
    initials: string;
    uf: string;
    name: string;
  } | null;
  /** Preenchido exatamente quando `institution` é `null`. */
  institutionError: {
    reason: ValidateExamImportInstitutionErrorReason;
    message: string;
  } | null;
  existingExam: { id: string; status: ContentStatus } | null;
  errors: ValidateExamImportError[];
}

/**
 * A questão que outra pessoa está editando não derruba o lote: ela volta com
 * o mesmo `code` do 409 (`CONTENT_EDIT_LOCKED`), e as demais seguem.
 */
export interface EditLockedQuestionResult {
  questionId: string;
  error: 'CONTENT_EDIT_LOCKED';
}

export type BulkDeleteQuestionsResult =
  | {
      questionId: string;
      /** `HARD_DELETED` = rascunho removido de fato; `TOMBSTONED` = soft delete. */
      mode: 'HARD_DELETED' | 'TOMBSTONED';
    }
  | EditLockedQuestionResult;

export interface MoveExamQuestionsInput {
  questionIds: string[];
  targetExamId: string;
  /** Ausente = mantém o status atual de cada questão. */
  publishMode?: 'KEEP_DRAFT' | 'PUBLISH';
}

export type MoveExamQuestionsResult =
  | {
      questionId: string;
      newPosition: number;
      published: boolean;
    }
  | EditLockedQuestionResult;

// --- Lock de edição ----------------------------------------------------------

/**
 * Um item por id pedido no `POST admin/content/edit-locks`, que é aquisição e
 * heartbeat ao mesmo tempo. `lockedBy` só vem no `LOCKED`; `name` falta quando
 * o perfil não resolve, e `since` é quando a outra pessoa pegou o lock.
 */
export interface ContentEditLockResult {
  contentId: string;
  status: 'ACQUIRED' | 'LOCKED' | 'NOT_FOUND';
  lockedBy?: {
    platformProfileId: string;
    name?: string;
    since: string;
  };
}
